import { useCallback, useEffect, useRef } from 'react';

/**
 * useDragSelect — 체크 상자를 **끌어서 여러 개 켜고 끄기** (2026-08-18).
 *
 * 요구: "워크스페이스에서 체크박스에 드래그 선택·해지가 가능하도록."
 * 하나씩 눌러 열 개를 고르는 것과, 눌러서 훑는 것의 차이다.
 *
 * 규칙은 파일 탐색기·메일함에서 쓰는 것과 같다 — **처음 누른 칸의 반대**가 그 드래그의
 * 모드가 된다. 꺼진 칸에서 시작하면 지나는 칸을 **켜고**, 켜진 칸에서 시작하면 **끈다**.
 *
 * ## 왜 pointerenter 가 아니라 elementFromPoint 인가
 *
 * 터치에서는 `pointerdown` 이 일어난 요소가 **암묵적으로 포인터를 캡처**한다(implicit
 * pointer capture). 그래서 손가락이 다른 칸 위로 가도 그 칸의 `pointerenter` 는 **영영
 * 안 온다** — 마우스로 개발하면 멀쩡한데 폰에서만 안 되는 종류의 버그다.
 * 그래서 좌표로 찾는다: window 의 `pointermove` 좌표를 `document.elementFromPoint` 로
 * 훑고, `[data-drag-select-id]` 를 단 조상을 찾는다. 마우스·터치·펜이 모두 같은 길을 탄다.
 *
 * ## 클릭 토글과 겹치지 않게
 *
 * 칸 하나를 그냥 누르는 것은 지금까지처럼 **click 이 처리**한다(이 hook 은 손대지 않는다).
 * 손가락이 {@link DRAG_THRESHOLD_PX} 넘게 움직인 뒤에야 "칠하기" 가 시작되고, 그때 처음
 * 누른 칸부터 모드를 입힌다. 칠하기가 일어났으면 뒤따르는 click 은 {@link consumeClick}
 * 이 한 번 삼킨다 — 안 그러면 시작 칸에서 손을 떼는 순간 방금 칠한 것이 도로 뒤집힌다.
 *
 * 키보드(Enter/Space)는 `pointerdown` 없이 click 만 오므로 이 hook 을 타지 않는다.
 */

/** 이만큼 움직이기 전에는 클릭으로 본다 — 누를 때의 손떨림이 칠하기로 오해되지 않게. */
const DRAG_THRESHOLD_PX = 4;

/** 칠하는 동안 글자가 선택되지 않게 body 에 붙이는 표식(CSS 에서 user-select 를 끈다). */
const DRAGGING_CLASS = 'ws-drag-selecting';

/**
 * 좌표로 찾을 대상에 다는 표식.
 *
 * **행 전체**에 단다(체크 상자에만 달면 빨리 끌 때 상자 사이 틈으로 행이 건너뛰어진다).
 * 시작은 체크 상자에서만 하므로(`startProps`) 행을 표식으로 써도 뜻이 흐려지지 않는다 —
 * 일단 상자에서 끌기 시작했으면 아래로 훑는 동안 열을 정확히 따라갈 필요가 없다.
 */
export const DRAG_SELECT_ATTR = 'data-drag-select-id';

interface DragState {
  /** 처음 누른 칸. */
  startId: string;
  /** 이 드래그가 입히는 값 — 처음 누른 칸의 반대. */
  on: boolean;
  startX: number;
  startY: number;
  /** 임계값을 넘어 실제로 칠하기 시작했는가. */
  painting: boolean;
}

export interface DragSelectApi {
  /** **끌기를 시작하는 곳**(체크 상자·그 칸)에 펼친다. */
  startProps: (id: string, checked: boolean) => {
    onPointerDown: (e: { clientX: number; clientY: number; button?: number }) => void;
  };
  /** **끌려 지나가는 곳**(행 전체)에 펼친다. 둘 다 붙여야 동작한다. */
  targetProps: (id: string) => { [DRAG_SELECT_ATTR]: string };
  /**
   * 방금 클릭이 드래그의 꼬리인가. true 면 호출부는 그 클릭을 통째로 삼킨다(한 번만 참).
   * 표/목록 뿌리에서 `onClickCapture` 로 쓰는 것이 가장 안전하다 — 체크 토글과 행 클릭
   * (세부정보 열기)을 **한 번에** 막는다.
   */
  consumeClick: () => boolean;
}

/**
 * @param setSelect 한 칸의 값을 **정하는** 함수(토글 아님). 같은 값으로 여러 번 불릴 수 있다.
 */
export function useDragSelect(setSelect: (id: string, on: boolean) => void): DragSelectApi {
  const drag = useRef<DragState | null>(null);
  const swallowClick = useRef(false);
  /** 최신 setSelect 를 본다 — 부모가 매 렌더 새 함수를 줘도 리스너를 다시 달지 않기 위해. */
  const setSelectRef = useRef(setSelect);
  setSelectRef.current = setSelect;

  const onMove = useCallback((e: PointerEvent) => {
    const d = drag.current;
    if (!d) return;

    if (!d.painting) {
      const moved = Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY);
      if (moved < DRAG_THRESHOLD_PX) return;
      // 여기서부터 칠하기 — 처음 누른 칸이 먼저 모드를 받는다.
      d.painting = true;
      swallowClick.current = true;
      document.body.classList.add(DRAGGING_CLASS);
      setSelectRef.current(d.startId, d.on);
    }

    // ⚠ 캡처 때문에 pointerenter 가 안 온다 — 좌표로 찾는다(파일 머리 주석).
    const el = document.elementFromPoint?.(e.clientX, e.clientY);
    const hit = el?.closest?.(`[${DRAG_SELECT_ATTR}]`);
    const id = hit?.getAttribute(DRAG_SELECT_ATTR);
    if (id) setSelectRef.current(id, d.on);
  }, []);

  const stop = useCallback(function stop(this: void) {
    drag.current = null;
    document.body.classList.remove(DRAGGING_CLASS);
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', stop);
    window.removeEventListener('pointercancel', stop);
  }, [onMove]);

  /*
   * 끌던 중에 화면이 바뀌면(폴더 이동·언마운트) window 리스너가 남는다. 남은 리스너는
   * 사라진 화면의 setSelect 를 계속 불러 유령 선택을 만든다 — cleanup 으로 stop 을 쓴다.
   */
  useEffect(() => stop, [stop]);

  const startProps = useCallback((id: string, checked: boolean) => ({
    onPointerDown: (e: { clientX: number; clientY: number; button?: number }) => {
      // 가운데·오른쪽 단추는 선택이 아니다(메뉴·붙여넣기).
      if (e.button !== undefined && e.button !== 0) return;
      swallowClick.current = false;
      drag.current = { startId: id, on: !checked, startX: e.clientX, startY: e.clientY, painting: false };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
    },
  }), [onMove, stop]);

  const targetProps = useCallback((id: string) => ({ [DRAG_SELECT_ATTR]: id }), []);

  const consumeClick = useCallback(() => {
    if (!swallowClick.current) return false;
    swallowClick.current = false;
    return true;
  }, []);

  return { startProps, targetProps, consumeClick };
}
